/**
 * Persist large damage photos outside sessionStorage quota limits.
 */

const DB_NAME = "pi-inspection-damage";
const STORE = "photos";
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("idb open failed"));
  });
}

export async function idbSetPhoto(productId, dataUrl) {
  if (!productId || !dataUrl) return false;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).put(dataUrl, String(productId));
    });
    db.close();
    return true;
  } catch {
    return false;
  }
}

export async function idbGetPhoto(productId) {
  if (!productId) return null;
  try {
    const db = await openDb();
    const value = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(String(productId));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return typeof value === "string" && value.startsWith("data:") ? value : null;
  } catch {
    return null;
  }
}

export async function idbClearPhoto(productId) {
  if (!productId) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).delete(String(productId));
    });
    db.close();
  } catch {
    /* ignore */
  }
}

/** In-memory fallback while the page stays alive */
const memoryPhotos = new Map();

export function memorySetPhoto(productId, dataUrl) {
  if (!productId || !dataUrl) return;
  memoryPhotos.set(String(productId), dataUrl);
}

export function memoryGetPhoto(productId) {
  if (!productId) return null;
  return memoryPhotos.get(String(productId)) || null;
}

export function memoryClearPhoto(productId) {
  if (!productId) return;
  memoryPhotos.delete(String(productId));
}

export async function persistDamagePhoto(productId, dataUrl) {
  if (!productId || !dataUrl || dataUrl === "__large__") return false;
  memorySetPhoto(productId, dataUrl);
  return idbSetPhoto(productId, dataUrl);
}

export async function loadDamagePhoto(productId) {
  const fromMem = memoryGetPhoto(productId);
  if (fromMem) return fromMem;
  return idbGetPhoto(productId);
}

export async function clearDamagePhoto(productId) {
  memoryClearPhoto(productId);
  await idbClearPhoto(productId);
}
